import { IconName } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: (is_mail?: boolean) => void
}

interface Props {
  confirmationModalState: ConfirmationModalState
  closeCallback: VoidFunction
}

const ConfirmationModal: FC<Props> = (props) => {
  const { t } = useTranslation()

  const {
    confirmationModalState,
    closeCallback,
  } = props

  const [scrollY, setScrollY] = useState<number>(0)
  const [isSendMail, setIsSendMail] = useState<boolean>(true)

  const buildTitle = (type: string) => {
    if (type === "reset") {
      return t('setting.member_modal.confirm_reset_password.title')
    }
    if (type === "delete") {
      return t('setting.member_modal.confirm_delete_member.title')
    }
    if (type === "disable") {
      return t('setting.member_modal.confirm_delete_member_mfa.title')
    }
    if (type === "active") {
      return "アカウントの復帰"
    }
    if (type === "inactive") {
      return "アカウントの休眠"
    }
    return ""
  }

  const buildBody = (type: string) => {
    if (type === "reset") {
      return (
        <>
          <p>
            {t('setting.member_modal.confirm_reset_password.member_header')}
            &nbsp;
            <span id="delete-member-name">{confirmationModalState.name}</span>
          </p>
          <p>{t('setting.member_modal.confirm_reset_password.confirm')}</p>
        </>
      )
    }
    if (type === "delete") {
      return (
        <>
          <p>
            {t('setting.member_modal.confirm_delete_member.member_header')}
            &nbsp;
            <span id="delete-member-name">{confirmationModalState.name}</span>
          </p>
          <p>
            {t('setting.member_modal.confirm_delete_member.confirm')}
            <br />
            {t('setting.member_modal.confirm_delete_member.confirm2')}
          </p>
        </>
      )
    }
    if (type === "disable") {
      return (
        <>
          <p>
            {t('setting.member_modal.confirm_delete_member_mfa.member_header')}
            &nbsp;
            <span id="delete-member-name">{confirmationModalState.name}</span>
          </p>
          <p>{t('setting.member_modal.confirm_delete_member_mfa.confirm')}</p>
        </>
      )
    }
    if (type === "active") {
      return (
        <>
          <p>
            <span>{confirmationModalState.name}</span>
            さんのアカウントを復帰します。
          </p>
          <p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
              }}
              onClick={() => setIsSendMail(!isSendMail)}
              role="none"
            >
              <FontAwesomeIcon
                icon={['far', `${isSendMail ? 'check-square' : 'square'}` as IconName]}
                className={`icon ${isSendMail ? "checked" : "no_check"}`}
                style={{
                  fontSize: "18px",
                  color: isSendMail ? "#08984C" : "#666666",
                }}
              />
              パスワード再設定メールを送信する
            </div>
          </p>
        </>
      )
    }
    if (type === "inactive") {
      return (
        <>
          <p>
            <span>{confirmationModalState.name}</span>
            さんのアカウントを休眠します。
          </p>
          <p>
            休眠状態にするとログインすることができなくなり、データの取得もおこないません。(今までのデータは残ります)
          </p>
          <p>
            詳しくは
            <a href="https://qasee.zendesk.com/hc/ja/articles/38720149953817" target="_blank" rel="noreferrer">
              こちらのガイド
            </a>
            をご参照ください。
          </p>
        </>
      )
    }
    return <></>
  }

  const buildButtonText = (type: string) => {
    if (type === "reset") {
      return t('form.component.send')
    }
    if (type === "delete") {
      return t('form.component.delete')
    }
    if (type === "disable") {
      return t('form.component.disable')
    }
    if (type === "active") {
      return "復帰する"
    }
    if (type === "inactive") {
      return "休眠する"
    }
    return ""
  }

  const submit = (type: string) => {
    if (type === "active") {
      confirmationModalState.callback(isSendMail)
    } else {
      confirmationModalState.callback()
    }
    closeCallback()
  }

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (confirmationModalState.display) {
      const scroll = window.scrollY
      html.classList.add('modal_open')
      html.style.top = `-${scroll}px`
      setScrollY(scroll)
    } else {
      html.classList.remove('modal_open')
      window.scrollTo(0, scrollY)
      setScrollY(0)
    }
  }, [confirmationModalState.display])

  return (
    <>
      {
        confirmationModalState.display && (
          <div className="modal_wrap">
            <div className={`confirmation_modal ${confirmationModalState.display || 'none'}`} style={{ width: "500px" }}>
              <div className="confirmation_modal_header">
                <div className="title">
                  {
                    buildTitle(confirmationModalState.type)
                  }
                </div>
                <button type="button" className="close_modal" onClick={closeCallback}>
                  &times;
                </button>
              </div>
              <div className="confirmation_modal_body">
                {
                  buildBody(confirmationModalState.type)
                }
              </div>
              <div className="confirmation_modal_footer">
                <button type="button" className="button cancel" onClick={closeCallback}>
                  {t('form.component.cancel')}
                </button>
                <button
                  type="button"
                  className={`button ${confirmationModalState.type}`}
                  onClick={() => submit(confirmationModalState.type)}
                >
                  {
                    buildButtonText(confirmationModalState.type)
                  }
                </button>
              </div>
            </div>
            <div className="close_screen gray" role="presentation" onClick={closeCallback} />
          </div>
        )
      }
    </>
  )
}

export default ConfirmationModal
